/**
 * OAuthCallback - Página de callback OAuth para Embedded Signup
 * 
 * Esta página é acessada quando o Meta redireciona após o fluxo de OAuth.
 * Ela processa os parâmetros da URL e comunica com a janela principal.
 * 
 * Cenários de uso:
 * 1. Popup mode: Janela pai recebe postMessage e processa
 * 2. In-app browser: Não há janela pai, salva state e redireciona
 * 3. Redirect mode: Usuário é redirecionado de volta ao app
 * 
 * URIs de redirecionamento válidos no Meta App:
 * - https://SEU_DOMINIO/oauth-callback
 * 
 * @see https://developers.facebook.com/docs/facebook-login/guides/advanced/manual-flow
 * @see https://developers.facebook.com/docs/whatsapp/embedded-signup
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Button,
  makeStyles
} from "@material-ui/core";
import {
  CheckCircle2 as CheckCircleIcon,
  AlertCircle as ErrorIcon,
  AlertTriangle as WarningIcon
} from "lucide-react";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    container: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: theme.palette.background.default,
      padding: theme.spacing(2)
    },
    paper: {
      padding: theme.spacing(4),
      maxWidth: 500,
      width: "100%",
      textAlign: "center",
      borderRadius: 14,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: dark ? "0 2px 8px rgba(0,0,0,0.25)" : "0 2px 8px rgba(0,0,0,0.06)"
    },
    icon: {
      fontSize: 64,
      marginBottom: theme.spacing(2)
    },
    successIcon: {
      color: theme.palette.success.main
    },
    errorIcon: {
      color: theme.palette.error.main
    },
    warningIcon: {
      color: theme.palette.warning.main
    },
    title: {
      marginBottom: theme.spacing(2),
      fontWeight: 600
    },
    message: {
      marginBottom: theme.spacing(3),
      color: theme.palette.text.secondary
    },
    countdown: {
      marginTop: theme.spacing(2),
      color: theme.palette.text.secondary
    },
    button: {
      marginTop: theme.spacing(2)
    },
    orRedirect: {
      marginTop: theme.spacing(1)
    },
    logo: {
      width: 120,
      marginBottom: theme.spacing(3)
    },
    details: {
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
      backgroundColor: dark ? theme.palette.action.selected : theme.palette.background.contrast,
      borderRadius: 10,
      textAlign: "left",
      border: `1px solid ${theme.palette.divider}`
    },
    detailItem: {
      display: "flex",
      justifyContent: "space-between",
      marginBottom: theme.spacing(1),
      "&:last-child": {
        marginBottom: 0
      }
    }
  };
});

// Estados do callback
const CALLBACK_STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
  CANCELLED: "cancelled"
};

const OAuthCallback = () => {
  const classes = useStyles();
  const [status, setStatus] = useState(CALLBACK_STATUS.LOADING);
  const [message, setMessage] = useState("");
  const [details, setDetails] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const [canClose, setCanClose] = useState(false);

  /**
   * Envia mensagem para a janela que abriu o popup
   */
  const notifyOpener = useCallback((data) => {
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({
          type: "WA_EMBEDDED_SIGNUP_OAUTH",
          ...data
        }, window.location.origin);
        return true;
      }
    } catch (err) {
      console.error("[OAuthCallback] Error posting message to opener:", err);
    }
    return false;
  }, []);

  /**
   * Processa os parâmetros da URL
   */
  useEffect(() => {
    const processCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      
      // Parâmetros do OAuth
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const error = urlParams.get("error");
      const errorReason = urlParams.get("error_reason");
      const errorDescription = urlParams.get("error_description");

      // Verificar se o usuário cancelou
      if (error === "access_denied" || errorReason === "user_denied") {
        setStatus(CALLBACK_STATUS.CANCELLED);
        setMessage(i18n.t("oauthCallback.cancelled") || "Você cancelou o processo de autorização.");
        notifyOpener({ 
          event: "CANCEL",
          error: "user_denied"
        });
        setCanClose(true);
        return;
      }

      // Verificar se houve erro
      if (error) {
        setStatus(CALLBACK_STATUS.ERROR);
        setMessage(errorDescription || i18n.t("oauthCallback.error") || "Ocorreu um erro durante a autorização.");
        setDetails({ error, errorReason, errorDescription });
        notifyOpener({ 
          event: "ERROR",
          error,
          errorReason,
          errorDescription
        });
        setCanClose(true);
        return;
      }

      // Verificar se recebeu o código
      if (!code) {
        setStatus(CALLBACK_STATUS.ERROR);
        setMessage(i18n.t("oauthCallback.noCode") || "Nenhum código de autorização recebido.");
        notifyOpener({ 
          event: "ERROR",
          error: "no_code"
        });
        setCanClose(true);
        return;
      }

      // Sucesso! Tentar enviar código para a janela principal
      const hasOpener = notifyOpener({ 
        event: "OAUTH_SUCCESS",
        code,
        state
      });

      if (hasOpener) {
        // Cenário 1: Popup mode - janela pai vai processar
        setStatus(CALLBACK_STATUS.SUCCESS);
        setMessage(i18n.t("oauthCallback.success") || "Autorização concluída com sucesso!");
        setDetails({ code: code.substring(0, 20) + "...", state });
        setCanClose(true);

        // Fechar automaticamente após countdown
        let count = 5;
        const timer = setInterval(() => {
          count--;
          setCountdown(count);
          if (count <= 0) {
            clearInterval(timer);
            window.close();
          }
        }, 1000);

        return () => clearInterval(timer);
      } else {
        // Cenário 2: In-app browser ou redirect mode
        // Salvar código em sessionStorage para processamento posterior
        try {
          sessionStorage.setItem("embedded_signup_oauth", JSON.stringify({
            code,
            state,
            timestamp: Date.now()
          }));
          
          setStatus(CALLBACK_STATUS.SUCCESS);
          setMessage(
            i18n.t("oauthCallback.successRedirect") || 
            "Autorização concluída! Clique no botão para continuar."
          );
          setDetails({ code: code.substring(0, 20) + "...", state });
          setCanClose(true);
          
          console.log("[OAuthCallback] No opener window - saved to sessionStorage");
        } catch (err) {
          console.error("[OAuthCallback] Error saving to sessionStorage:", err);
          setStatus(CALLBACK_STATUS.ERROR);
          setMessage(
            i18n.t("oauthCallback.storageError") || 
            "Erro ao salvar dados. Por favor, tente novamente."
          );
          setCanClose(true);
        }
      }
    };

    processCallback();
  }, [notifyOpener]);

  /**
   * Fecha a janela manualmente
   */
  const handleClose = () => {
    window.close();
  };

  /**
   * Redireciona para a página principal (se não conseguir fechar)
   */
  const handleRedirect = () => {
    window.location.href = "/connections";
  };

  const renderIcon = () => {
    switch (status) {
      case CALLBACK_STATUS.SUCCESS:
        return <CheckCircleIcon className={`${classes.icon} ${classes.successIcon}`} />;
      case CALLBACK_STATUS.ERROR:
        return <ErrorIcon className={`${classes.icon} ${classes.errorIcon}`} />;
      case CALLBACK_STATUS.CANCELLED:
        return <WarningIcon className={`${classes.icon} ${classes.warningIcon}`} />;
      default:
        return <CircularProgress size={64} />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case CALLBACK_STATUS.SUCCESS:
        return i18n.t("oauthCallback.successTitle") || "Autorização Concluída!";
      case CALLBACK_STATUS.ERROR:
        return i18n.t("oauthCallback.errorTitle") || "Erro na Autorização";
      case CALLBACK_STATUS.CANCELLED:
        return i18n.t("oauthCallback.cancelledTitle") || "Autorização Cancelada";
      default:
        return i18n.t("oauthCallback.loadingTitle") || "Processando...";
    }
  };

  return (
    <Box className={classes.container}>
      <Paper className={classes.paper} elevation={0}>
        {/* Logo opcional */}
        {/* <img src="/logo.png" alt="Logo" className={classes.logo} /> */}
        
        {/* Ícone de status */}
        {renderIcon()}
        
        {/* Título */}
        <Typography variant="h5" className={classes.title}>
          {getTitle()}
        </Typography>
        
        {/* Mensagem */}
        <Typography variant="body1" className={classes.message}>
          {message || (status === CALLBACK_STATUS.LOADING && (i18n.t("oauthCallback.processing") || "Aguarde enquanto processamos sua autorização..."))}
        </Typography>

        {/* Detalhes (para debug) */}
        {details && process.env.NODE_ENV === "development" && (
          <Box className={classes.details}>
            <Typography variant="caption" color="textSecondary">
              Detalhes (apenas em desenvolvimento):
            </Typography>
            {Object.entries(details).map(([key, value]) => (
              <Box key={key} className={classes.detailItem}>
                <Typography variant="caption"><strong>{key}:</strong></Typography>
                <Typography variant="caption">{value || "N/A"}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Countdown para fechar */}
        {status === CALLBACK_STATUS.SUCCESS && canClose && countdown > 0 && (
          <Typography variant="body2" className={classes.countdown}>
            {i18n.t("oauthCallback.closingIn", { seconds: countdown }) || `Esta janela será fechada em ${countdown} segundos...`}
          </Typography>
        )}

        {/* Botões */}
        {canClose && (
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClose}
              className={classes.button}
            >
              {i18n.t("oauthCallback.closeWindow") || "Fechar Janela"}
            </Button>
            
            <Typography variant="caption" display="block" className={classes.orRedirect}>
              {i18n.t("oauthCallback.orRedirect") || "ou"}
            </Typography>
            
            <Button
              variant="text"
              color="primary"
              onClick={handleRedirect}
              size="small"
            >
              {i18n.t("oauthCallback.goToConnections") || "Ir para Conexões"}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default OAuthCallback;
